import type { ReactNode } from 'react';
import { replaceAny } from 'replace-any';

import { PLURAL_PLACEHOLDER_NAME, REGEXP_PLACEHOLDER, REGEXP_WRAPPER } from './constants';
import { lookup } from './lookup';
import reactStrategy from './strategies/react';
import { interpolateAll } from './string';
import type { PhraseMap, PlaceholderMap, SafeStrMap, Strategy } from './types';

const localizeNumericPlaceholders = (placeholders: PlaceholderMap, locale: string) =>
  Object.keys(placeholders).reduce((updatedPlaceholders, currentPlaceholder) => {
    const currentValue = placeholders[currentPlaceholder];

    if (typeof currentValue === 'number') {
      return {
        ...updatedPlaceholders,
        [currentPlaceholder]: Number(currentValue).toLocaleString(locale),
      };
    }

    return {
      ...updatedPlaceholders,
      [currentPlaceholder]: currentValue,
    };
  }, {});

/**
 * Returns the interpolated string for the specified key. If the string is not found, it throws.
 *
 * @param key String key.
 * @param placeholders Placeholders to be interpolated in the string.
 */
type Interpolate = (locale: string, phrases: PhraseMap, key: string, placeholders?: PlaceholderMap) => string;
export const interpolate: Interpolate = (locale, phrases, key, placeholders = {}) => {
  const str = lookup(locale, phrases, key, placeholders[PLURAL_PLACEHOLDER_NAME]);

  const placeholdersWithLocalizedPlurals = localizeNumericPlaceholders(placeholders, locale);

  return interpolateAll(REGEXP_PLACEHOLDER, str, placeholdersWithLocalizedPlurals);
};

/**
 * Returns the interpolated string for the specified key. If the string is not found, it returns the key.
 *
 * @param key String key.
 * @param placeholders Placeholders to be interpolated in the string.
 */
type InterpolateSafe = (locale: string, phrases: PhraseMap, key: string, placeholders?: PlaceholderMap) => string;
export const interpolateSafe: InterpolateSafe = (locale, phrases, key, placeholders) => {
  try {
    return interpolate(locale, phrases, key, placeholders);
  } catch {
    return '';
  }
};

/**
 * Returns the interpolated string for the specified key after having applied the specified strategy to any wrapper tags
 * found in the string. If the string is not found, it throws.
 *
 * @param strategy Strategy to be applied on wrapper tags.
 * @param key String key.
 * @param placeholders Placeholders to be interpolated in the string.
 */
type InterpolateWithStrategy = <T>(
  locale: string,
  phrases: PhraseMap,
  strategy: Strategy<T>,
  key: string,
  placeholders?: PlaceholderMap
) => (string | T)[];
export const interpolateWithStrategy: InterpolateWithStrategy = (locale, phrases, strategy, key, placeholders = {}) => {
  const wrappers = [{ regexp: REGEXP_WRAPPER, replace: strategy(placeholders) }];
  const translation = interpolate(locale, phrases, key, placeholders);

  return replaceAny(wrappers, translation);
};

/**
 * Returns the interpolated string for the specified key after having applied the specified strategy to any wrapper tags
 * found in the string. If the string is not found, it returns an empty string.
 *
 * @param strategy Strategy to be applied on wrapper tags.
 * @param key String key.
 * @param placeholders Placeholders to be interpolated in the string.
 */
type InterpolateWithStrategySafe = <T>(
  locale: string,
  phrases: PhraseMap,
  strategy: Strategy<T>,
  key: string,
  placeholders?: PlaceholderMap
) => (string | T)[] | '';
export const interpolateWithStrategySafe: InterpolateWithStrategySafe = (
  locale,
  phrases,
  strategy,
  key,
  placeholders
) => {
  try {
    return interpolateWithStrategy(locale, phrases, strategy, key, placeholders);
  } catch {
    return '';
  }
};

/**
 * Returns the interpolated string for the specified key after having applied the React strategy to any wrapper tags
 * found in the string. If the string is not found, it throws.
 *
 * @param key String key.
 * @param placeholders Placeholders to be interpolated in the string.
 */
type InterpolateWithReactStrategy = (
  locale: string,
  phrases: PhraseMap,
  key: string,
  placeholders?: PlaceholderMap
) => ReactNode;
export const interpolateWithReactStrategy: InterpolateWithReactStrategy = (locale, phrases, key, placeholders) =>
  interpolateWithStrategy(locale, phrases, reactStrategy, key, placeholders);

/**
 * Returns the interpolated string for the specified key after having applied the specified strategy to any wrapper tags
 * found in the string. If the string is not found, it returns an empty string.
 *
 * @param key String key.
 * @param placeholders Placeholders to be interpolated in the string.
 */
type InterpolateWithReactStrategySafe = (
  locale: string,
  phrases: PhraseMap,
  key: string,
  placeholders?: PlaceholderMap
) => ReactNode;
export const interpolateWithReactStrategySafe: InterpolateWithReactStrategySafe = (
  locale,
  phrases,
  key,
  placeholders
) => {
  try {
    return interpolateWithReactStrategy(locale, phrases, key, placeholders);
  } catch {
    return '';
  }
};

/**
 * This is used to return a string interpolator function for all yammer-string feature files. The auto-generated /dist
 * files in yammer-strings include the list of strings which need to be interpolated with interpolateWithReactStrategySafe.
 * They're passed in here for each file. Other than that, the /dist files are identical.
 * @param safeStrs
 * @param feature
 * @returns
 */
export const getStringInterpolator = (
  safeStrs: SafeStrMap,
  feature: string
): ((locale: string, phrases: PhraseMap) => Record<string, (args: PlaceholderMap) => ReactNode | string>) & {
  readonly feature: string;
} => {
  const StringInterpolator = function (locale: string, phrases: PhraseMap) {
    const placeholderObj = {};

    return new Proxy(placeholderObj, {
      get(_target, prop: string) {
        return function (args: PlaceholderMap) {
          if (safeStrs[prop]) {
            return interpolateWithReactStrategySafe(locale, phrases, prop, args);
          }

          return interpolateSafe(locale, phrases, prop, args);
        };
      },
    });
  };
  StringInterpolator.feature = feature;

  return StringInterpolator as ((
    locale: string,
    phrases: PhraseMap
  ) => Record<string, (args: PlaceholderMap) => ReactNode | string>) & { readonly feature: string };
};
